import { DatePicker } from '@components/common/DatePicker';
import { useAppSelector } from '@hooks/useRedux';
import { selectAccommodationProperty } from '@store/accommodation';
import { selectOutletId } from '@store/auth/authSelectors';
import { Download, RefreshCw } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import {
  RevenueReportRow,
  fetchRevenueReport,
} from '../../../../services/accommodationService';
import { apiError } from '../../../../util/notify';
import {
  currentMonthRange,
  exportToCsv,
  fmtAmount,
  fmtDate,
} from './reportUtils';

const { from: initFrom, to: initTo } = currentMonthRange();

const STATUS_COLOURS: Record<string, string> = {
  confirmed: 'bg-blue-100 text-blue-700',
  checked_in: 'bg-green-100 text-green-700',
  checked_out: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-600',
  no_show: 'bg-orange-100 text-orange-700',
};

export const RevenueReportPage: React.FC = () => {
  const property = useAppSelector(selectAccommodationProperty);
  const userOutletId = useAppSelector(selectOutletId);
  // Use property outlet when available, fall back to user outlet, then null (all outlets)
  const outletId = property?.outlet_id ?? userOutletId ?? null;
  const currency = property?.currency ?? '';

  const [dateFrom, setDateFrom] = useState(initFrom);
  const [dateTo, setDateTo] = useState(initTo);
  const [rows, setRows] = useState<RevenueReportRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchRevenueReport(outletId, dateFrom, dateTo);
      setRows(data);
      setLoaded(true);
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setIsLoading(false);
    }
  }, [outletId, dateFrom, dateTo]);

  const totalRevenue = rows.reduce((s, r) => s + r.total_amount, 0);
  const totalCollected = rows.reduce((s, r) => s + r.amount_paid, 0);
  const totalBalance = rows.reduce((s, r) => s + r.balance_due, 0);
  const totalNights = rows.reduce((s, r) => s + (r.nights ?? 0), 0);

  const handleCsv = () => {
    exportToCsv(
      `revenue-report-${dateFrom}-${dateTo}`,
      rows.map((r) => ({
        'Booking ID': r.booking_id,
        Guest:
          `${r.client_first_name ?? ''} ${r.client_last_name ?? ''}`.trim() ||
          '—',
        Email: r.client_email ?? '',
        Nationality: r.client_nationality ?? '',
        'Check-in': r.check_in,
        'Check-out': r.check_out,
        Nights: r.nights,
        Status: r.status,
        Source: r.source_name ?? 'Direct',
        [`Revenue (${currency})`]: fmtAmount(r.total_amount),
        [`Collected (${currency})`]: fmtAmount(r.amount_paid),
        [`Balance (${currency})`]: fmtAmount(r.balance_due),
      })),
    );
  };

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-gray-200 bg-white p-4">
        <DatePicker
          mode="single"
          id="rev-date-from"
          label="Check-in from"
          value={dateFrom}
          onChange={setDateFrom}
        />
        <DatePicker
          mode="single"
          id="rev-date-to"
          label="Check-in to"
          value={dateTo}
          onChange={setDateTo}
        />
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Loading…' : 'Run report'}
        </button>
        {loaded && rows.length > 0 && (
          <button
            type="button"
            onClick={handleCsv}
            className="flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Download size={14} />
            Export CSV
          </button>
        )}
      </div>

      {loaded && (
        <>
          {/* KPI summary */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                label: 'Bookings',
                value: String(rows.length),
                sub: 'in period',
              },
              {
                label: 'Total Revenue',
                value: `${currency} ${fmtAmount(totalRevenue)}`,
                sub: 'excl. cancelled',
              },
              {
                label: 'Collected',
                value: `${currency} ${fmtAmount(totalCollected)}`,
                sub: `Balance: ${currency} ${fmtAmount(totalBalance)}`,
              },
              {
                label: 'Total Nights',
                value: String(totalNights),
                sub: 'room-nights sold',
              },
            ].map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border border-gray-200 bg-white p-4"
              >
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="mt-1 text-xl font-semibold text-gray-900">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">{card.sub}</p>
              </div>
            ))}
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-3">
              <h4 className="text-sm font-semibold text-gray-900">
                Revenue detail — {fmtDate(dateFrom)} to {fmtDate(dateTo)}
              </h4>
            </div>
            {rows.length === 0 ? (
              <div className="flex h-28 items-center justify-center text-sm text-gray-400">
                No bookings found for this period.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      {[
                        'Guest',
                        'Check-in',
                        'Check-out',
                        'Nights',
                        'Source',
                        'Status',
                        `Revenue (${currency})`,
                        `Collected (${currency})`,
                        `Balance (${currency})`,
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((r) => {
                      const guest =
                        `${r.client_first_name ?? ''} ${r.client_last_name ?? ''}`.trim() ||
                        '—';
                      return (
                        <tr key={r.booking_id} className="hover:bg-gray-50/50">
                          <td className="px-4 py-2.5 font-medium text-gray-900">
                            {guest}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                            {fmtDate(r.check_in)}
                          </td>
                          <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">
                            {fmtDate(r.check_out)}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {r.nights}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {r.source_name ?? 'Direct'}
                          </td>
                          <td className="px-4 py-2.5">
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_COLOURS[r.status] ?? 'bg-gray-100 text-gray-600'}`}
                            >
                              {r.status.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium text-gray-900">
                            {fmtAmount(r.total_amount)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-gray-700">
                            {fmtAmount(r.amount_paid)}
                          </td>
                          <td
                            className={`px-4 py-2.5 text-right font-medium ${r.balance_due > 0 ? 'text-red-600' : 'text-gray-400'}`}
                          >
                            {r.balance_due > 0 ? fmtAmount(r.balance_due) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-gray-200 bg-gray-50">
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-2.5 text-xs font-semibold text-gray-600"
                      >
                        Total ({rows.length} bookings)
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-gray-900">
                        {fmtAmount(totalRevenue)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-semibold text-gray-700">
                        {fmtAmount(totalCollected)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-sm font-bold text-red-600">
                        {totalBalance > 0 ? fmtAmount(totalBalance) : '—'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {!loaded && !isLoading && (
        <div className="flex h-40 items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
          Select a date range and click &ldquo;Run report&rdquo; to load data.
        </div>
      )}
    </div>
  );
};
